/**
 * Материализация расписания приёмов в базу.
 *
 * Планировщик (`dose-scheduler.ts`) остаётся чистым, а вся работа с хранилищем
 * собрана здесь. Сценарий один: при открытии таблетницы и после любой правки
 * курса база догоняется до состояния «на горизонте 30 дней есть все дозы».
 *
 * Операция идемпотентна — повторный запуск ничего не создаёт заново, поэтому её
 * безопасно вызывать при каждом входе на экран. Идемпотентность держится на
 * транзакции вокруг пары «прочитать существующие дозы → дописать недостающие»:
 * без неё одновременные вызовы дублировали приёмы.
 */

import { doseEventsRepo } from '@/entities/dose/model/repository';
import {
  DOSE_HORIZON_DAYS,
  isSchedulable,
  pastCourseGap,
  type PlannedDose,
  type ScheduleSlotsForDate,
  planDoseSync,
} from '@/entities/medication/model/dose-scheduler';
import {
  isDoseAccountable,
  isDoseAhead,
  isDoseMissed,
} from '@/entities/medication/model/dose-status';
import { materializeRoutines } from '@/entities/routine/model/day-schedule';
import { scheduleSlotsForDate, isBoundToMeals } from '@/entities/medication/model/meal-schedule';
import { db } from '@/shared/db/database';
import type { DoseEvent, Medication } from '@/shared/db/schema';
import { getCurrentUserId } from '@/shared/db/session-context';
import { addDays, type IsoDate, momentAt, today as todayIso } from '@/shared/lib/datetime';

/** Насколько глубоко в прошлое ищем неотмеченные дозы для пометки «пропущено». */
const MISSED_LOOKBACK_DAYS = 45;

/**
 * Приёмы расписания по дням для курса, привязанного к еде.
 *
 * Правила питания и переопределения на весь горизонт читаются разом, а дальше
 * режим каждого дня материализуется на месте. Так сдвиг обеда на один день
 * двигает только свой приём, а правка правила — все будущие.
 */
async function mealSlotResolver(
  medication: Medication,
  from: IsoDate,
  to: IsoDate,
): Promise<ScheduleSlotsForDate> {
  const userId = getCurrentUserId();
  if (!userId) return () => [];

  const [routines, overrides] = await Promise.all([
    db.routines.where('[userId+kind]').equals([userId, 'meal']).toArray(),
    db.routineOverrides.where('userId').equals(userId).toArray(),
  ]);

  const alive = routines.filter((routine) => routine.deletedAt === null);
  const byDate = new Map<IsoDate, typeof overrides>();
  for (const override of overrides) {
    if (override.deletedAt !== null || override.date < from || override.date > to) continue;
    byDate.set(override.date, [...(byDate.get(override.date) ?? []), override]);
  }

  return (date) =>
    scheduleSlotsForDate(medication, materializeRoutines(alive, byDate.get(date) ?? [], date));
}

/*
  Режим питания читается внутри той же транзакции, что и запись приёмов, и
  поэтому попадает в её область. Иначе между чтением режима и записью успевает
  пройти правка обеда: запущенная раньше синхронизация досчитывает план по
  устаревшему времени и возвращает уже исправленные приёмы обратно.

  Курсам без привязки лишние таблицы в области не нужны: транзакция берёт на
  них блокировку, а правка режима дня к их расписанию отношения не имеет.
*/
function scheduleTables(bound: boolean) {
  return bound
    ? [db.doseEvents, db.outbox, db.routines, db.routineOverrides]
    : [db.doseEvents, db.outbox];
}

/**
 * Запись приёма из плана расписания.
 *
 * Принятым он заводится только за прошедший день, о котором человек сказал
 * «уже пила»: время приёма — плановое, другого у приложения нет.
 */
function doseRecord(dose: PlannedDose, taken: boolean) {
  return {
    medicationId: dose.medicationId,
    date: dose.date,
    mealRoutineId: dose.mealRoutineId,
    scheduledMin: dose.scheduledMin,
    plannedAmount: dose.plannedAmount,
    status: taken ? ('taken' as const) : ('pending' as const),
    takenAt: taken ? momentAt(dose.date, dose.scheduledMin) : null,
    actualAmount: taken ? dose.plannedAmount : null,
    snoozedUntil: null,
    skipReason: null,
    note: '',
  };
}

async function loadDoses(medicationId: string, from: IsoDate, to: IsoDate): Promise<DoseEvent[]> {
  const doses = await db.doseEvents
    .where('[medicationId+date]')
    .between([medicationId, from], [medicationId, to], true, true)
    .toArray();

  return doses.filter((dose) => dose.deletedAt === null);
}

/**
 * Догоняет расписание одного препарата до горизонта.
 * Возвращает число созданных доз — удобно для отладки и тестов.
 *
 * Чтение и запись выполняются одной транзакцией, и это не перестраховка.
 * Идемпотентность здесь держится на том, что план строится по уже
 * существующим дозам, — а между «прочитали» и «записали» есть промежуток.
 * Два одновременных вызова для одного препарата оба видели «доз ещё нет» и оба
 * их создавали: в ленте дня приём двоился. Достичь этого было легко —
 * сохранение курса запускает синхронизацию из формы, а эффект на экране
 * таблетницы поднимает её же на новой ревизии; в режиме разработки React
 * вызывает эффекты дважды. Транзакция выстраивает вызовы в очередь: второй
 * читает уже созданные дозы и не делает ничего.
 */
export async function syncMedicationSchedule(
  medication: Medication,
  today: IsoDate = todayIso(),
): Promise<number> {
  /*
    Курс без расписания синхронизировать нечем, и трогать его приёмы нельзя:
    у «по необходимости» они все заведены руками. Пустой план объявил бы их
    лишними, и намеченный на вечер приём исчезал бы от правки названия курса.

    Убрать построенное расписание, когда курс переводят в «по необходимости»
    или отправляют в архив, — отдельное действие (`dropFutureDoses`): там это
    решение осознанное, а здесь оно вышло бы побочным.
  */
  if (!isSchedulable(medication)) return 0;

  const horizonEnd = addDays(today, DOSE_HORIZON_DAYS);
  const bound = isBoundToMeals(medication);

  return db.transaction('rw', scheduleTables(bound), async () => {
    /*
      Курс, привязанный к еде, времени в себе не хранит: оно считается из режима
      каждого дня. Режим читается один раз на весь горизонт — правил питания
      единицы, а дней тридцать, и ходить в базу на каждый день незачем.
    */
    const slotsForDate = bound ? await mealSlotResolver(medication, today, horizonEnd) : undefined;

    const existing = await loadDoses(medication.id, today, horizonEnd);
    const plan = planDoseSync(medication, existing, today, horizonEnd, slotsForDate);

    if (plan.create.length > 0) {
      await doseEventsRepo.createMany(plan.create.map((dose) => doseRecord(dose, false)));
    }

    /*
      Приёмы, у которых уехало время: еда сдвинулась, а приём тот же самый.
      Правка на месте, а не «удалить и создать заново»: пересоздание теряет
      заметку, отложенный час и заведённое уведомление, а в журнале один приём
      выглядел бы двумя.
    */
    for (const { dose, scheduledMin, mealRoutineId } of plan.move) {
      await doseEventsRepo.update(dose.id, { scheduledMin, mealRoutineId });
    }

    // Дозы, выпавшие из расписания после правки курса. Удаление мягкое,
    // отмеченные приёмы планировщик в этот список не отдаёт.
    for (const dose of plan.obsolete) {
      await doseEventsRepo.remove(dose.id);
    }

    return plan.create.length;
  });
}

/**
 * Достраивает приёмы прошедших дней курса, начатого задним числом.
 *
 * Обычная синхронизация идёт от сегодняшнего дня и в прошлое не заглядывает —
 * иначе она возвращала бы приёмы, которые человек удалил из журнала как
 * ошибочные. Поэтому прошлое достраивается только по явному решению: курс
 * завели или повторили с прошедшей даты, начало курса сдвинули раньше.
 *
 * `taken` — человек сказал, что эти таблетки уже выпил. Из упаковки они не
 * списываются: остаток он указывает тот, что лежит сейчас. Без отметки приёмы
 * заводятся ожидающими и уходят в пропущенные — исправить их можно в журнале.
 *
 * `builtFrom` — с какого дня приёмы уже строились (см. `pastCourseGap`).
 * Возвращает созданные записи: отмена действия должна убрать и их.
 */
export async function backfillPastDoses(
  medication: Medication,
  options: { builtFrom?: IsoDate; taken: boolean },
  today: IsoDate = todayIso(),
): Promise<string[]> {
  if (!isSchedulable(medication)) return [];

  const gap = pastCourseGap(medication.startDate, options.builtFrom ?? today, today);
  if (!gap) return [];

  const bound = isBoundToMeals(medication);

  return db.transaction('rw', scheduleTables(bound), async () => {
    const slotsForDate = bound ? await mealSlotResolver(medication, gap.from, gap.to) : undefined;
    const existing = await loadDoses(medication.id, gap.from, gap.to);
    const plan = planDoseSync(medication, existing, gap.from, gap.to, slotsForDate);

    const created = await doseEventsRepo.createMany(
      plan.create.map((dose) => doseRecord(dose, options.taken)),
    );
    return created.map((dose) => dose.id);
  });
}

/** Убирает достроенные приёмы — при отмене действия, которое их завело. */
export async function removeBackfilledDoses(ids: readonly string[]): Promise<void> {
  if (ids.length === 0) return;

  await db.transaction('rw', db.doseEvents, db.outbox, async () => {
    for (const id of ids) await doseEventsRepo.remove(id);
  });
}

/**
 * Помечает просроченные приёмы прошлых дней как пропущенные.
 *
 * Сегодняшние опоздания в базу не пишутся: человек ещё может принять лекарство,
 * и статус в журнале не должен опережать реальность. На экране такие дозы
 * подсвечиваются как просроченные вычислением на лету.
 */
export async function markMissedDoses(today: IsoDate = todayIso()): Promise<number> {
  const userId = getCurrentUserId();
  if (!userId) return 0;

  const from = addDays(today, -MISSED_LOOKBACK_DAYS);
  const yesterday = addDays(today, -1);
  if (yesterday < from) return 0;

  // Отбор и пометка — одной транзакцией. Синхронизация запускается и по входу
  // на вкладку, и после правки курса; два параллельных прохода без транзакции
  // помечали одни и те же дозы дважды, накручивая ревизию и очередь обмена.
  return db.transaction('rw', db.doseEvents, db.medications, db.outbox, async () => {
    const doses = await db.doseEvents
      .where('[userId+date]')
      .between([userId, from], [userId, yesterday], true, true)
      .toArray();

    // Приёмы курсов «по необходимости» человек намечает сам, чтобы ему
    // напомнили. Спрашивать с него за то, что таблетка не понадобилась, — не
    // дело приложения, поэтому пропущенными они не становятся.
    const accountable = new Set(
      (await db.medications.where('userId').equals(userId).toArray())
        .filter(isDoseAccountable)
        .map((medication) => medication.id),
    );

    const stale = doses.filter(
      (dose) =>
        dose.deletedAt === null &&
        accountable.has(dose.medicationId) &&
        isDoseMissed(dose, today, 0),
    );

    for (const dose of stale) {
      await doseEventsRepo.update(dose.id, { status: 'missed' });
    }

    return stale.length;
  });
}

/** Полная синхронизация: расписания всех активных курсов плюс разбор просрочек. */
export async function syncAllSchedules(
  medications: Medication[],
  today: IsoDate = todayIso(),
): Promise<void> {
  for (const medication of medications) {
    if (!isSchedulable(medication)) continue;
    await syncMedicationSchedule(medication, today);
  }
  await markMissedDoses(today);
}

/**
 * Убирает будущие неотмеченные дозы препарата — при архивации курса.
 * История остаётся: удаляются только те приёмы, которые ещё не наступили.
 */
export async function dropFutureDoses(
  medicationId: string,
  today: IsoDate = todayIso(),
): Promise<void> {
  const horizonEnd = addDays(today, DOSE_HORIZON_DAYS);

  await db.transaction('rw', db.doseEvents, db.outbox, async () => {
    const doses = await loadDoses(medicationId, today, horizonEnd);

    for (const dose of doses) {
      if (isDoseAhead(dose)) {
        await doseEventsRepo.remove(dose.id);
      }
    }
  });
}
